import type { Signal } from '@angular/core';
import { asObject, emitChangesOnRequest } from '@freelancer/operators';
import { sameElements, toSingletonSignal } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { Subject, combineLatest, firstValueFrom, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import type { RequestDataPayload } from './actions';
import { DatastoreDocument } from './datastore-document';
import type { RecursivePartial } from './helpers';
import { arrayIsShallowEqual } from './helpers';
import type { RequestStatus } from './request-status-handler.service';
import type { StoreBackendInterface } from './store-backend.interface';
import type {
  BackendDeleteResponse,
  BackendPushResponse,
  BackendSetResponse,
  BackendUpdateResponse,
} from './store-backend.model';
import type { QueryResultWithMetadata } from './store.helpers';
import {
  getEqualOrInIdsFromQuery,
  isIdQuery,
  isTransformedIdsQuery,
  removeEqualOrInIdsFromQuery,
  stringifyReference,
} from './store.helpers';
import type {
  ApproximateTotalCountType,
  DatastoreCollectionType,
  DatastorePushCollectionType,
  DatastoreSetCollectionType,
  MaybeDatastoreDeleteCollectionType,
  MaybeDatastorePushCollectionType,
  MaybeDatastoreSetCollectionType,
  MaybeDatastoreUpdateCollectionType,
  PushDocumentType,
  Reference,
  SetDocumentType,
} from './store.model';

export interface CollectionConfig {
  /**
   * Enable cursor-based pagination for this collection.
   *
   * - `true` — shorthand for `{ mergePages: true }` (accumulate pages on
   *   loadMore)
   * - `{ mergePages: true }` (default) — loadMore appends the next page to
   *   the existing results
   * - `{ mergePages: false }` — loadMore replaces the current results with
   *   the next page
   */
  readonly cursorPagination?:
    | true
    | {
        readonly mergePages?: boolean;
      };
}

export interface ValueChangesLessOften<C extends DatastoreCollectionType> {
  readonly values$: Observable<readonly C['DocumentType'][]>;
  readonly hasChanges$: Observable<boolean>;
  readonly valuesSignal: Signal<readonly C['DocumentType'][] | undefined>;
  readonly hasChangesSignal: Signal<boolean | undefined>;
  refresh(): void;
}

export class DatastoreCollection<C extends DatastoreCollectionType> {
  private valueChanges$: Observable<readonly C['DocumentType'][]>;
  private hasMore$: Observable<boolean>;
  private signalStore: {
    valueChanges?: Signal<readonly C['DocumentType'][] | undefined>;
    valueChangesAsObject?: Signal<
      | {
          readonly [id: string]: C['DocumentType'];
        }
      | undefined
    >;
    status?: Signal<RequestStatus<C> | undefined>;
    approximateTotalCount?: Signal<ApproximateTotalCountType<C> | undefined>;
    hasMore?: Signal<boolean | undefined>;
  } = {};

  constructor(
    private requestDataConfig: {
      readonly dedupeWindowTime: number;
      readonly batchWindowTime: number;
      readonly maxBufferTime: number;
    },
    private ref$: Observable<Reference<C>>,
    private storeBackend: StoreBackendInterface,
    public status$: Observable<RequestStatus<C>>,
    private queryResult$: Observable<
      QueryResultWithMetadata<C> & {
        readonly request: RequestDataPayload<C> | undefined;
      }
    >,
    valueChangesOverride$: Observable<readonly C['DocumentType'][]> | undefined,
    private approximateTotalCount$: Observable<
      ApproximateTotalCountType<C> | undefined
    >,
    private loadMoreHandler?: (() => void) | undefined,
  ) {
    /**
     * This should be moved to `valueChanges()` but it causes issues
     * when you do `valueChanges() | flasync` in a template.
     * We should wean people off doing this, but until then
     * this needs to be in the constructor.
     */
    this.valueChanges$ =
      valueChangesOverride$ ??
      this.queryResult$.pipe(
        map(queryResult =>
          queryResult.documentsWithMetadata.map(
            documentWithMetadata => documentWithMetadata.rawDocument,
          ),
        ),
        distinctUntilChanged(arrayIsShallowEqual),
      );
    this.hasMore$ = this.queryResult$.pipe(
      map(queryResult => !!queryResult.nextCursor),
      distinctUntilChanged(),
    );
  }

  /**
   * Return an array of all documents in a collection for a given query,
   * emitting changes as the occur. If the collection errors, it returns
   * an an empty array ([])
   */
  valueChanges(): Observable<readonly C['DocumentType'][]> {
    return this.status$.pipe(
      map(status => !!status.error),
      distinctUntilChanged(),
      switchMap(hasError => (hasError ? of([]) : this.valueChanges$)),
    );
  }

  /**
   * Return an observable that emits a boolean indicating if there are more results to be loaded for this collection.
   *
   * FIXME: T328242 - This is at the mercy of the backend implementation's correctness.
   * TODO: T328822 - This only works with cursor-based pagination as of the time of writing.
   */
  hasMore(): Observable<boolean> {
    return this.hasMore$;
  }

  get hasMoreSignal(): Signal<boolean> {
    return toSingletonSignal(this.signalStore, 'hasMore', this.hasMore(), {
      initialValue: false,
    });
  }

  /**
   * Trigger loading more results for this collection. Only applicable if `hasMore()` returns true.
   *
   * TODO: T328822 - This only works with cursor-based pagination as of the time of writing.
   */
  loadMore(): void {
    this.loadMoreHandler?.();
  }

  get valueChangesSignal(): Signal<readonly C['DocumentType'][]> {
    return toSingletonSignal(
      this.signalStore,
      'valueChanges',
      this.valueChanges(),
      { initialValue: [] },
    );
  }

  /**
   * Return a map of all documents in a collection for a given query,
   * emitting changes as the occur.
   */
  valueChangesAsObject(): Observable<{
    readonly [id: string]: C['DocumentType'];
  }> {
    return this.valueChanges().pipe(asObject());
  }

  get valueChangesAsObjectSignal(): Signal<
    | {
        readonly [id: string]: C['DocumentType'];
      }
    | undefined
  > {
    return toSingletonSignal(
      this.signalStore,
      'valueChangesAsObject',
      this.valueChangesAsObject(),
    );
  }

  /**
   * Return an array of all documents in a collection for a given query,
   * emitting changes to documents as they occur, but only adding/removing
   * documents when requested by the `notifier$`, or when the query changes.
   *
   * Returns an object with both the `values` themselves and a `changes` flag
   * as to if there are new elements that have been added or removed.
   *
   * This DOES NOT return cached values older than a `maximumStaleness`
   * so that on loading a page with an old cache you don't first get a
   * cached value that immediately "changes". This staleness CAN NOT
   * be set to be smaller than the internal datastore network request batching
   * or deduplication logic.
   */
  valueChangesLessOften({
    maximumStaleness = 300_000, // Five minutes
  }: {
    readonly maximumStaleness?: number;
  } = {}): ValueChangesLessOften<C> {
    if (maximumStaleness <= this.requestDataConfig.batchWindowTime) {
      throw new Error(
        'Cannot request a staleness less than the window for batching requests',
      );
    }
    if (maximumStaleness <= this.requestDataConfig.dedupeWindowTime) {
      throw new Error(
        'Cannot request a staleness less than the window for de-duplicating requests',
      );
    }

    const recently = Date.now() - maximumStaleness;
    const notifier$ = new Subject<void>();
    const refresh = (): void => notifier$.next();

    const valuesAndHasChanges$ = this.queryResult$.pipe(
      /**
       * Don't return old queries.
       *
       * This effectively disables the initial cache while emitting whenever
       * the list changes. This relies on that fact that subscribing for the
       * first time makes a new network request. Subsequent subscribing is fine
       * due to the `publishReplay`/`refCount` at the end.
       *
       * It will emit not just for network fetches but also if the list is
       * updated in any way (network fetch, websockets or user actions).
       *
       * That said, when requesting ids we don't actually construct the query
       * in the store and so we don't have a `timeUpdated`. For `id` based
       * queries you should probably not be using `valueChangesLessOften`
       * but if you are it's probably reasonable to just return what you have.
       */
      filter(
        queryResult =>
          queryResult.isTransferredFromSSR ||
          (queryResult.request &&
            isTransformedIdsQuery(queryResult.request.ref)) ||
          (queryResult.timeUpdated !== undefined &&
            queryResult.timeUpdated > recently),
      ),

      // Emit a new list either when asked, or when the query changes
      emitChangesOnRequest(
        queryResult =>
          queryResult.documentsWithMetadata.map(
            documentWithMetadata => documentWithMetadata.rawDocument,
          ),
        /* Emit the ref$ when ref$ OR notifier$ emits. */
        combineLatest([this.ref$, notifier$.pipe(startWith(undefined))]).pipe(
          map(([ref, notifier]) => ref),
        ),

        /**
         * When the datastore emits data with the query matching
         * what the user requests, then emit the first data emitted.
         */
        (queryResult, ref) => {
          if (queryResult.request === undefined) {
            return false;
          }
          if (isIdQuery(ref.query)) {
            return sameElements(
              getEqualOrInIdsFromQuery(ref.query) ?? [],
              queryResult.request.ref.path.ids ?? [],
            );
          }
          return (
            queryResult.request !== undefined &&
            stringifyReference(queryResult.request.ref) ===
              stringifyReference(moveIdsFromQuery(ref))
          );
        },
      ),

      /**
       * We want someone who subscribes late to still get the last emission
       * (as the filter would otherwise mean that they would not).
       */
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const values$ = valuesAndHasChanges$.pipe(
      map(x => x.values),
      distinctUntilChanged(arrayIsShallowEqual),
    );

    const hasChanges$ = valuesAndHasChanges$.pipe(
      map(x => x.hasChanges),
      distinctUntilChanged(),
    ); // FIXME: T267853 - add distinctUntilChanged

    /**
     * We have a separate signal store here because this function is parameterized and seems to be
     * callable multiple times which if we use the signal store outside would just cause
     * the signals to be overwritten and not scoped to the call of the function.
     */
    const signalStore: {
      values?: Signal<readonly C['DocumentType'][] | undefined>;
      hasChanges?: Signal<boolean | undefined>;
    } = {};

    return {
      values$,
      hasChanges$,
      get valuesSignal(): Signal<readonly C['DocumentType'][] | undefined> {
        return toSingletonSignal(signalStore, 'values', values$);
      },
      get hasChangesSignal(): Signal<boolean | undefined> {
        return toSingletonSignal(signalStore, 'hasChanges', hasChanges$);
      },
      refresh,
    };
  }

  approximateTotalCount(): Observable<
    ApproximateTotalCountType<C> | undefined
  > {
    return this.approximateTotalCount$;
  }

  get approximateTotalCountSignal(): Signal<
    ApproximateTotalCountType<C> | undefined
  > {
    return toSingletonSignal(
      this.signalStore,
      'approximateTotalCount',
      this.approximateTotalCount$,
    );
  }

  push(
    // Make calling this function fail if you haven't defined `C['Backend']['Push']`
    document: C extends DatastorePushCollectionType
      ? PushDocumentType<C>
      : never,
  ): Promise<BackendPushResponse<MaybeDatastorePushCollectionType<C>>> {
    return firstValueFrom(
      this.ref$.pipe(
        take(1),
        map(ref => ref as unknown as Reference<C>), // Unfortunate type casting
        switchMap(ref => this.storeBackend.push(ref, document)),
      ),
    );
  }

  set(
    id: number | string,
    // Make calling this function fail if you haven't defined `C['Backend']['Set']`
    document: C extends DatastoreSetCollectionType ? SetDocumentType<C> : never,
  ): Promise<BackendSetResponse<MaybeDatastoreSetCollectionType<C>>> {
    return firstValueFrom(
      this.ref$.pipe(
        take(1),
        map(ref => ref as unknown as Reference<C>), // Unfortunate type casting
        switchMap(ref => this.storeBackend.set(ref, id, document)),
      ),
    );
  }

  update(
    id: number | string,
    // Make calling this function fail if you haven't defined `C['Backend']['Update']`
    delta: C['Backend']['Update'] extends never
      ? never
      : RecursivePartial<C['DocumentType']>,
  ): Promise<BackendUpdateResponse<MaybeDatastoreUpdateCollectionType<C>>> {
    return firstValueFrom(
      this.ref$.pipe(
        take(1),
        map(
          // Unfortunate type casting
          ref =>
            ref as unknown as Reference<MaybeDatastoreUpdateCollectionType<C>>,
        ),
        switchMap(ref =>
          this.storeBackend.update<MaybeDatastoreUpdateCollectionType<C>>(
            ref,
            id,
            delta,
          ),
        ),
      ),
    );
  }

  remove(
    // Make calling this function fail if you haven't defined `C['Backend']['Delete']`
    id: C['Backend']['Delete'] extends never ? never : number | string,
  ): Promise<BackendDeleteResponse<MaybeDatastoreDeleteCollectionType<C>>> {
    return firstValueFrom(
      this.ref$.pipe(
        take(1),
        map(
          // Unfortunate type casting
          ref =>
            ref as unknown as Reference<MaybeDatastoreDeleteCollectionType<C>>,
        ),
        switchMap(ref => this.storeBackend.delete(ref, id)),
      ),
    );
  }

  toDatastoreDocumentList(): Observable<readonly DatastoreDocument<C>[]> {
    return this.queryResult$.pipe(
      map(collections =>
        collections.documentsWithMetadata.map(
          collection =>
            new DatastoreDocument<C>(
              this.ref$.pipe(
                map(ref => ({
                  path: {
                    ...ref.path,
                    ids: [collection.rawDocument.id.toString()] as const,
                  },
                })),
              ),
              this.storeBackend,
              this.status$,
              of(collection.rawDocument),
            ),
        ),
      ),
    );
  }

  get statusSignal(): Signal<RequestStatus<C> | undefined> {
    return toSingletonSignal(this.signalStore, 'status', this.status$);
  }
}

/**
 * This function should not be here, if it should exist at all.
 * We should at least have two types for a `Reference`, one
 * with the ids in the query, and one without, so that you
 * can tell by the type system if this has been transformed
 * or not.
 *
 * Then it should become more standard which one is used where.
 *
 * This exists here to make the `ref$` and `queryResult.request.ref` both
 * the same so we can compare them. It would be better to make
 * them the same in what is passed in, but that might be a
 * breaking change and so we do this here for now.
 */
function moveIdsFromQuery<C extends DatastoreCollectionType>(
  ref: Reference<C>,
): Reference<C> {
  return {
    ...ref,
    query: removeEqualOrInIdsFromQuery(ref.query),
    path: {
      ...ref.path,
      ids: getEqualOrInIdsFromQuery(ref.query),
    },
  };
}
